import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import AppHeader from "./layout/app-header";
import { NavigationItemData } from "./layout/mobile-menu";
import { useAuth } from "../contexts/AuthContext";
import { INTL_NAV_ITEMS } from "../config/nav";
import { ordersKeys } from "../hooks/queryKeys";

interface LayoutProps {
  children: React.ReactNode;
}

/**
 * Main application layout wrapper for the Intl Orders Portal.
 * Manages theme state, page titles, and renders the header with navigation.
 */
const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { user, logout, hasRole } = useAuth();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("theme");
    return (
      saved === "dark" ||
      (saved == null && window.matchMedia("(prefers-color-scheme: dark)").matches)
    );
  });

  // Toggle theme and persist to localStorage
  const toggleTheme = (): void => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem("theme", newTheme ? "dark" : "light");
  };

  // Apply theme to document element
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add("dark");
      root.classList.remove("light");
    } else {
      root.classList.add("light");
      root.classList.remove("dark");
    }
  }, [isDarkMode]);

  // Update page title based on current route
  useEffect(() => {
    const pathname = location.pathname;
    let title = "Intl Orders Portal";

    if (pathname.startsWith("/orders")) {
      title = "Orders - Intl Orders Portal";
    } else if (pathname.startsWith("/stock")) {
      title = "Stock - Intl Orders Portal";
    } else if (pathname.startsWith("/reports")) {
      title = "Reports - Intl Orders Portal";
    } else if (pathname.startsWith("/admin")) {
      title = "Admin - Intl Orders Portal";
    }

    document.title = title;
  }, [location.pathname]);

  // Invalidate orders queries on route change so back-navigation gets fresh data
  const queryClient = useQueryClient();
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ordersKeys.all,
      refetchType: "active",
    });
  }, [location.pathname, queryClient]);

  // Role gate applied HERE (the single consumption site for INTL_NAV_ITEMS) so
  // nav.ts stays declarative and desktop-navigation.tsx / mobile-menu.tsx never
  // need their own role awareness — they just render whatever `navigation` they're given.
  const navigation: NavigationItemData[] = INTL_NAV_ITEMS.filter(
    (view) => view.requiredRole === undefined || hasRole(view.requiredRole)
  ).map((view) => ({
    name: view.name,
    href: view.path,
    current: location.pathname.startsWith(view.path),
  }));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <AppHeader
        navigation={navigation}
        user={user}
        isDarkMode={isDarkMode}
        onThemeToggle={toggleTheme}
        onLogout={() => void logout()}
      />

      {/* Main content */}
      <main className="flex-1 relative z-0">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
